import type { Workspace, Note } from "./types";

/* Activity hub — client mirror of api/src/reporting/activity.ts. One unified,
 * chronological timeline for an account, fused from its event log, signals,
 * tasks, playbook runs, notifications, survey responses, renewal movement, and
 * notes (human + AI). Byte-compatible with the API. */

export type ActivityKind = "note" | "signal" | "task" | "playbook" | "survey" | "renewal" | "milestone" | "meeting" | "event";
export type ActivityTone = "good" | "watch" | "risk" | "neutral";

export interface ActivityEntry {
  id: string; at: string; kind: ActivityKind; tone: ActivityTone;
  title: string; detail?: string; actor?: string; ai?: boolean;
}

const iso = (s: string) => (s.includes("T") ? s : `${s}T12:00:00Z`);

export function buildActivity(ws: Workspace, accountId: string, notes: Note[] = ws.notes ?? []): ActivityEntry[] {
  const a = ws.accounts.find((x) => x.id === accountId);
  if (!a) return [];
  const nameOf = (id?: string) => ws.users.find((u) => u.id === id)?.name;
  const out: ActivityEntry[] = [];

  for (const e of a.timeline) {
    const kind: ActivityKind = e.kind === "play" ? "playbook" : e.kind === "milestone" ? "milestone" : e.kind === "meeting" ? "meeting" : e.kind === "signal" ? "signal" : "event";
    out.push({ id: `tl:${e.id}`, at: iso(e.at), kind, tone: e.band ?? "neutral", title: e.title, detail: e.detail });
  }
  for (const s of ws.signals.filter((s) => s.accountId === accountId)) {
    out.push({ id: `sig:${s.id}`, at: iso(s.at), kind: "signal", tone: s.band === "positive" ? "good" : s.band === "risk" ? "risk" : "watch",
      title: s.title, detail: `${s.source} · ${s.detail}${s.status !== "open" ? ` · ${s.status}` : ""}` });
  }
  for (const t of ws.tasks.filter((t) => t.accountId === accountId)) {
    out.push({ id: `task:${t.id}`, at: iso(t.createdAt), kind: "task", tone: t.status === "done" ? "good" : "neutral",
      title: `Task: ${t.title}`, detail: `${t.status === "done" ? "Completed" : `Due ${t.due}`} · ${t.priority} priority`, actor: nameOf(t.ownerId) });
  }
  for (const r of ws.playbookRuns.filter((r) => r.accountId === accountId)) {
    out.push({ id: `run:${r.id}`, at: iso(r.startedAt), kind: "playbook", tone: r.status === "completed" ? "good" : "neutral",
      title: `Playbook: ${r.playbookName}`, detail: `${r.status}${r.completedAt ? ` · finished ${r.completedAt.slice(0, 10)}` : ""}` });
  }
  for (const n of ws.notifications.filter((n) => n.accountId === accountId)) {
    out.push({ id: `ntf:${n.id}`, at: iso(n.at), kind: "event", tone: "neutral", title: n.title, detail: n.body });
  }
  const surveyName = new Map(ws.surveys.map((s) => [s.id, s.name]));
  for (const r of ws.surveyResponses.filter((r) => r.accountId === accountId)) {
    out.push({ id: `srv:${r.id}`, at: iso(r.at), kind: "survey", tone: r.score >= 9 ? "good" : r.score <= 6 ? "risk" : "watch",
      title: `${surveyName.get(r.surveyId) ?? "Survey"} response: ${r.score}`, detail: r.comment || undefined, actor: r.respondent });
  }
  for (const rn of ws.renewals.filter((r) => r.accountId === accountId)) {
    out.push({ id: `rnw:${rn.id}`, at: iso(rn.updatedAt), kind: "renewal", tone: rn.stage === "closed_won" ? "good" : rn.stage === "closed_lost" ? "risk" : "watch",
      title: `Renewal ${rn.stage.replace(/_/g, " ")}`, detail: `${rn.category} · renews ${rn.renewalDate}` });
  }
  for (const n of notes.filter((n) => n.accountId === accountId)) {
    out.push({ id: `note:${n.id}`, at: iso(n.at), kind: "note", tone: "neutral", title: n.ai ? "AI note" : "Note", detail: n.body, actor: n.authorName, ai: n.ai });
  }

  return out.sort((x, y) => +new Date(y.at) - +new Date(x.at));
}
